import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const validatePhone = (phone) => /^(?:\+55)?\d{2}9\d{8}$/.test(phone);
const validateEmail = (email) => /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email);
const validatePassword = (password) => ({ length: password.length >= 6 });

const FIELD_LABELS = {
  fullName: "Nome completo",
  email: "E-mail",
  phone: "Celular",
};

const Settings = () => {
  const [initialData, setInitialData] = useState({
    fullName: "",
    email: "",
    phone: "",
  });
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [editable, setEditable] = useState({
    fullName: false,
    email: false,
    phone: false,
  });
  const [errors, setErrors] = useState({});
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });

  const passwordRules = useMemo(
    () => validatePassword(formData.newPassword || ""),
    [formData.newPassword]
  );

  const loadProfile = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      setLoadingProfile(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/user-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Não foi possível carregar os dados atuais.");
      }

      setInitialData({
        fullName: data.fullName || "",
        email: data.email || "",
        phone: data.phone || "",
      });
      setFormData((prev) => ({
        ...prev,
        fullName: data.fullName || "",
        email: data.email || "",
        phone: data.phone || "",
      }));
    } catch (error) {
      setAlert({
        open: true,
        message: error.message || "Erro ao carregar dados do usuário.",
        severity: "error",
      });
    } finally {
      setLoadingProfile(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const toggleEdit = (field) => {
    setEditable((prev) => ({ ...prev, [field]: !prev[field] }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
    if (editable[field]) {
      setFormData((prev) => ({ ...prev, [field]: initialData[field] || "" }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {};
    const nextErrors = {};

    const fullName = formData.fullName.trim();
    const email = formData.email.trim();
    const phone = formData.phone.trim();
    const currentPassword = formData.currentPassword.trim();
    const newPassword = formData.newPassword;
    const confirmNewPassword = formData.confirmNewPassword;

    if (editable.fullName && fullName && fullName !== initialData.fullName) {
      payload.fullName = fullName;
    }

    if (editable.email && email && email !== initialData.email) {
      if (!validateEmail(email)) {
        nextErrors.email = "Digite um e-mail válido.";
      } else {
        payload.email = email;
      }
    }

    if (editable.phone && phone && phone !== initialData.phone) {
      if (!validatePhone(phone)) {
        nextErrors.phone = "Use: 41992331209 ou +5541992331209";
      } else {
        payload.phone = phone;
      }
    }

    const hasPasswordData = currentPassword || newPassword || confirmNewPassword;
    if (hasPasswordData) {
      if (!currentPassword) {
        nextErrors.currentPassword = "Informe sua senha atual.";
      }
      if (!newPassword) {
        nextErrors.newPassword = "Informe a nova senha.";
      } else if (!passwordRules.length) {
        nextErrors.newPassword = "A nova senha deve ter pelo menos 6 caracteres.";
      }
      if (newPassword !== confirmNewPassword) {
        nextErrors.confirmNewPassword = "As senhas não coincidem.";
      }

      if (
        !nextErrors.currentPassword &&
        !nextErrors.newPassword &&
        !nextErrors.confirmNewPassword
      ) {
        payload.currentPassword = currentPassword;
        payload.password = newPassword;
      }
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    if (Object.keys(payload).length === 0) {
      setAlert({
        open: true,
        message: "Nenhuma alteração para salvar.",
        severity: "info",
      });
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/update-user`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Erro ao atualizar dados.");
      }

      setAlert({
        open: true,
        message: data.message || "Dados atualizados com sucesso!",
        severity: "success",
      });
      setEditable({ fullName: false, email: false, phone: false });
      setFormData((prev) => ({
        ...prev,
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      }));
      await loadProfile();
    } catch (error) {
      setAlert({
        open: true,
        message: error.message || "Erro inesperado ao salvar.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, py: 1 }}>
      <Paper sx={{ maxWidth: 680, mx: "auto", p: { xs: 2, sm: 3 } }}>
        <Typography variant="h5" gutterBottom>
          Configurações do Usuário
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
          Clique na caneta para editar cada dado.
        </Typography>

        {loadingProfile ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            {["fullName", "email", "phone"].map((field) => (
              <Box key={field} sx={{ mb: 1.5 }}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                  {FIELD_LABELS[field]}
                </Typography>

                {editable[field] ? (
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <TextField
                      name={field}
                      value={formData[field]}
                      onChange={handleChange}
                      placeholder={initialData[field] || "Não informado"}
                      error={!!errors[field]}
                      helperText={errors[field]}
                      fullWidth
                      size="small"
                    />
                    <IconButton onClick={() => toggleEdit(field)}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      border: "1px solid rgba(255,255,255,0.16)",
                      borderRadius: 2,
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <Typography sx={{ opacity: 0.95 }}>
                      {initialData[field] || "Não informado"}
                    </Typography>
                    <IconButton size="small" onClick={() => toggleEdit(field)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            ))}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Alterar senha
            </Typography>

            <TextField
              label="Senha atual"
              name="currentPassword"
              type={showCurrentPassword ? "text" : "password"}
              value={formData.currentPassword}
              onChange={handleChange}
              error={!!errors.currentPassword}
              helperText={errors.currentPassword}
              fullWidth
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowCurrentPassword((prev) => !prev)} edge="end">
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Nova senha"
              name="newPassword"
              type={showNewPassword ? "text" : "password"}
              value={formData.newPassword}
              onChange={handleChange}
              error={!!errors.newPassword}
              helperText={errors.newPassword}
              fullWidth
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNewPassword((prev) => !prev)} edge="end">
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {formData.newPassword && (
              <List sx={{ mt: 0.5, mb: 1, py: 0 }}>
                <ListItem sx={{ px: 0, py: 0.2 }}>
                  <ListItemText
                    primary="Pelo menos 6 caracteres"
                    primaryTypographyProps={{
                      fontSize: 13,
                      color: passwordRules.length ? "success.main" : "error.main",
                    }}
                  />
                </ListItem>
              </List>
            )}

            <TextField
              label="Confirmar nova senha"
              name="confirmNewPassword"
              type={showConfirmNewPassword ? "text" : "password"}
              value={formData.confirmNewPassword}
              onChange={handleChange}
              error={!!errors.confirmNewPassword}
              helperText={errors.confirmNewPassword}
              fullWidth
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmNewPassword((prev) => !prev)}
                      edge="end"
                    >
                      {showConfirmNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button type="submit" variant="contained" disabled={saving} sx={{ mt: 2 }}>
              {saving ? "Salvando..." : "Salvar alterações"}
            </Button>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={alert.open}
        autoHideDuration={4500}
        onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
